/**
 * /control/inversiones · M-08 mínimo (Fase 18.6)
 */
import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { Coins, Plus, DollarSign, RefreshCw, AlertTriangle } from "lucide-react";
import {
    INV_ESTADOS, INSTRUMENTO_LABEL, REGIMEN_LABEL,
    fmtMXN, fmtPct, fmtFecha,
} from "./_common";

export default function Inversiones() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [fids, setFids] = useState([]);
    const [form, setForm] = useState({
        fideicomiso_id: "",
        tipo_instrumento: "cete",
        regimen: "supletorio_prudencial",
        monto_mxn: 1_000_000,
        fecha_inversion: new Date().toISOString().slice(0, 10),
        fecha_vencimiento: new Date(Date.now() + 90 * 86400000).toISOString().slice(0, 10),
        tasa_rendimiento: 0.105,
        intermediario: "BBVA Casa de Bolsa",
        notas: "",
    });

    const load = async () => {
        const r = await api.get("/control/inversiones");
        setItems(r.data || []);
    };
    const loadFids = async () => {
        try {
            const r = await api.get("/fideicomisos");
            const arr = Array.isArray(r.data) ? r.data : r.data.items || [];
            setFids(arr.map(f => ({ id: f.id, label: f.numero_fideicomiso || f.numero_interno || f.id })));
        } catch { setFids([]); }
    };
    useEffect(() => { load(); loadFids(); }, []);

    const crear = async () => {
        if (!form.fideicomiso_id) { toast.error("Fideicomiso requerido"); return; }
        try {
            await api.post("/control/inversiones", {
                ...form,
                fecha_inversion: new Date(form.fecha_inversion).toISOString(),
                fecha_vencimiento: form.fecha_vencimiento
                    ? new Date(form.fecha_vencimiento).toISOString() : null,
                monto_mxn: Number(form.monto_mxn),
                tasa_rendimiento: Number(form.tasa_rendimiento),
            });
            toast.success("Inversión creada");
            setOpen(false); load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error");
        }
    };

    const liquidar = async (inv) => {
        const rend = prompt("Rendimientos generados MXN:", "0");
        if (rend === null) return;
        try {
            await api.post(`/control/inversiones/${inv.id}/liquidar`, {
                rendimientos_mxn: Number(rend),
                referencia: `LIQ-${inv.folio_inversion}`,
            });
            toast.success(`Inversión liquidada · rend ${rend}`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error");
        }
    };

    return (
        <div className="space-y-6" data-testid="inv-list">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-3xl font-serif font-semibold" style={{ fontFamily: "'Fraunces', serif" }}>
                        Inversiones del Patrimonio · M-08
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        Régimen pactado o <b>supletorio prudencial</b> (gubernamentales ≤180 días).
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={load}><RefreshCw className="h-4 w-4" /></Button>
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button className="bg-[color:var(--coral)] hover:bg-[color:var(--coral)]/90 text-white"
                                data-testid="inv-nueva">
                                <Plus className="h-4 w-4 mr-1" /> Nueva inversión
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Nueva inversión</DialogTitle>
                                <DialogDescription>
                                    Si régimen=supletorio_prudencial, instrumento debe ser gubernamental con plazo ≤180 días.
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-3">
                                <div>
                                    <label className="text-xs font-semibold">Fideicomiso</label>
                                    <Select value={form.fideicomiso_id}
                                        onValueChange={v => setForm({ ...form, fideicomiso_id: v })}>
                                        <SelectTrigger data-testid="inv-sel-fid"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {fids.map(f => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-xs font-semibold">Instrumento</label>
                                        <Select value={form.tipo_instrumento}
                                            onValueChange={v => setForm({ ...form, tipo_instrumento: v })}>
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {Object.entries(INSTRUMENTO_LABEL).map(([k, v]) =>
                                                    <SelectItem key={k} value={k}>{v}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold">Régimen</label>
                                        <Select value={form.regimen}
                                            onValueChange={v => setForm({ ...form, regimen: v })}>
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {Object.entries(REGIMEN_LABEL).map(([k, v]) =>
                                                    <SelectItem key={k} value={k}>{v}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs font-semibold">Monto MXN</label>
                                    <Input type="number" value={form.monto_mxn}
                                        onChange={e => setForm({ ...form, monto_mxn: e.target.value })} />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-xs font-semibold">Fecha inversión</label>
                                        <Input type="date" value={form.fecha_inversion}
                                            onChange={e => setForm({ ...form, fecha_inversion: e.target.value })} />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold">Fecha vencimiento</label>
                                        <Input type="date" value={form.fecha_vencimiento}
                                            onChange={e => setForm({ ...form, fecha_vencimiento: e.target.value })} />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-xs font-semibold">Tasa (decimal)</label>
                                        <Input type="number" step="0.0001" value={form.tasa_rendimiento}
                                            onChange={e => setForm({ ...form, tasa_rendimiento: e.target.value })} />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold">Intermediario</label>
                                        <Input value={form.intermediario}
                                            onChange={e => setForm({ ...form, intermediario: e.target.value })} />
                                    </div>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                                <Button onClick={crear} data-testid="inv-crear-btn">Crear</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            <Card>
                <CardContent className="p-0">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/40 text-xs uppercase">
                            <tr>
                                <th className="px-3 py-2 text-left">Folio</th>
                                <th className="px-3 py-2 text-left">Instrumento</th>
                                <th className="px-3 py-2 text-left">Régimen</th>
                                <th className="px-3 py-2 text-right">Monto</th>
                                <th className="px-3 py-2 text-right">Tasa</th>
                                <th className="px-3 py-2 text-left">Vence</th>
                                <th className="px-3 py-2 text-left">Estado</th>
                                <th className="px-3 py-2 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 && (
                                <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">
                                    Sin inversiones.
                                </td></tr>
                            )}
                            {items.map(inv => {
                                const cfg = INV_ESTADOS[inv.estado] || { label: inv.estado, cls: "" };
                                return (
                                    <tr key={inv.id} className="border-t">
                                        <td className="px-3 py-2 font-mono text-xs">{inv.folio_inversion}</td>
                                        <td className="px-3 py-2 text-xs">{INSTRUMENTO_LABEL[inv.tipo_instrumento]}</td>
                                        <td className="px-3 py-2 text-xs">
                                            {inv.regimen === "supletorio_prudencial"
                                                ? <Badge className="bg-sky-100 text-sky-900 border-sky-300 border">Supletorio</Badge>
                                                : <Badge className="bg-slate-100 text-slate-800 border-slate-200 border">Pactado</Badge>}
                                        </td>
                                        <td className="px-3 py-2 text-right text-xs">{fmtMXN(inv.monto_mxn)}</td>
                                        <td className="px-3 py-2 text-right text-xs">{fmtPct(inv.tasa_rendimiento)}</td>
                                        <td className="px-3 py-2 text-xs">{fmtFecha(inv.fecha_vencimiento)}</td>
                                        <td className="px-3 py-2"><Badge className={cfg.cls}>{cfg.label}</Badge></td>
                                        <td className="px-3 py-2 text-right">
                                            {inv.estado === "vigente" && (
                                                <Button size="sm" variant="outline"
                                                    onClick={() => liquidar(inv)}
                                                    data-testid={`inv-liq-${inv.id}`}>
                                                    <DollarSign className="h-3 w-3" />
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </CardContent>
            </Card>

            <p className="text-xs text-muted-foreground flex items-start gap-1">
                <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                Régimen supletorio prudencial: sólo CETE/Bono M/UDIBONO con plazo ≤180 días.
            </p>
        </div>
    );
}
